(() => {
  const dateString = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  const initialize = (root) => {
    if (root.dataset.giftCardReady) return;
    const form = root.querySelector('form');
    if (!form) return;
    root.dataset.giftCardReady = 'true';

    const preview = root.querySelector('[data-gift-preview]');
    const designLabel = root.querySelector('[data-gift-design-label]');
    const recipient = root.querySelector('[data-gift-recipient]');
    const deliveries = root.querySelectorAll('[data-gift-delivery]');
    const designs = root.querySelector('[data-gift-designs]');
    const classic = root.querySelector('[data-gift-classic]');
    const occasion = root.querySelector('[data-gift-occasion]');
    const defaultDesign = root.querySelector('[data-gift-design]');
    const date = root.querySelector('[data-gift-date]');
    const offset = root.querySelector('[data-gift-offset]');
    const message = root.querySelector('[data-gift-message]');
    const suggestion = root.querySelector('[data-gift-suggestion]');
    let previewRequest = 0;

    const info = root.querySelector('[data-gift-info]');
    const desktop = window.matchMedia('(min-width: 990px)');
    // Match the product page: wheel input over either column scrolls the options
    // first, handing remaining movement back to the page at either boundary.
    root.addEventListener('wheel', (event) => {
      if (!desktop.matches || !event.cancelable || event.ctrlKey || !info) return;
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('textarea, select, dialog, [aria-modal="true"]')) return;
      const nested = target?.closest('[data-gift-designs]');
      if (nested && nested.scrollHeight > nested.clientHeight + 1) {
        const canScroll = event.deltaY < 0 ? nested.scrollTop > 0 : nested.scrollTop < nested.scrollHeight - nested.clientHeight - 1;
        if (canScroll) return;
      }
      const max = info.scrollHeight - info.clientHeight;
      if (max <= 1) return;
      let delta = event.deltaY;
      if (event.deltaMode === 1) delta *= 16;
      if (event.deltaMode === 2) delta *= info.clientHeight;
      const previous = info.scrollTop;
      const next = Math.min(Math.max(previous + delta, 0), max);
      const consumed = next - previous;
      if (Math.abs(consumed) < 0.5) return;
      event.preventDefault();
      info.scrollTop = next;
      const remaining = delta - consumed;
      if (Math.abs(remaining) >= 0.5) window.scrollBy({ top: remaining, behavior: 'auto' });
    }, { passive: false });

    const updateDates = () => {
      const today = new Date();
      const lastDay = new Date(today);
      lastDay.setDate(lastDay.getDate() + 90);
      date.min = dateString(today);
      date.max = dateString(lastDay);
      offset.value = String(today.getTimezoneOffset());
    };

    const updateMessage = () => {
      root.querySelector('[data-gift-count]').textContent = String(message.value.length);
    };

    const updateDesign = async ({ fillMessage = false } = {}) => {
      const choice = root.querySelector('[data-gift-design]:checked');
      if (!choice) return;
      designLabel.value = choice.dataset.label;
      designLabel.disabled = false;
      suggestion.hidden = !choice.dataset.message;
      if (fillMessage) {
        message.value = (choice.dataset.message || '').slice(0, 200);
        updateMessage();
      }
      const request = ++previewRequest;
      const incoming = new Image();
      incoming.src = choice.value;
      try {
        await Promise.race([
          incoming.decode(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Image timed out')), 8000))
        ]);
        if (request !== previewRequest) return;
        preview.src = incoming.src;
        preview.alt = choice.dataset.label;
      } catch {
        // Keep the loaded artwork in place if the selected preview cannot load.
      }
    };

    const updateDelivery = () => {
      const isRecipient = root.querySelector('[data-gift-delivery]:checked').value === 'recipient';
      recipient.hidden = !isRecipient;
      recipient.querySelectorAll('input, textarea').forEach((input) => { input.disabled = !isRecipient; });
      root.querySelector('[data-gift-self-note]').hidden = isRecipient;
      updateDates();
      updateMessage();
    };

    const showOccasions = (show) => {
      designs.hidden = !show;
      classic.setAttribute('aria-pressed', String(!show));
      occasion.setAttribute('aria-pressed', String(show));
      occasion.setAttribute('aria-expanded', String(show));
    };
    root.querySelector('[data-gift-design-mode]').hidden = false;
    showOccasions(true);
    classic.addEventListener('click', () => {
      const designChanged = !defaultDesign.checked;
      defaultDesign.checked = true;
      showOccasions(false);
      updateDesign({ fillMessage: designChanged });
    });
    occasion.addEventListener('click', () => showOccasions(true));

    root.querySelector('[data-gift-delivery-choice]').hidden = false;
    root.querySelectorAll('[data-gift-design]').forEach((input) => input.addEventListener('change', () => updateDesign({ fillMessage: true })));
    deliveries.forEach((input) => input.addEventListener('change', updateDelivery));
    message.addEventListener('input', updateMessage);
    date.addEventListener('focus', updateDates);
    suggestion.addEventListener('click', () => {
      const choice = root.querySelector('[data-gift-design]:checked');
      if (message.value.trim() && !window.confirm('Replace your message with the suggested greeting?')) return;
      message.value = (choice?.dataset.message || '').slice(0, 200);
      updateMessage();
      message.focus();
    });

    const emailPreview = root.querySelector('[data-gift-email-preview]');
    const emailTemplate = root.querySelector('[data-gift-email-template]');
    if (emailPreview && emailTemplate) {
      emailPreview.hidden = false;
      emailPreview.addEventListener('click', () => {
        const email = new DOMParser().parseFromString(JSON.parse(emailTemplate.textContent), 'text/html');
        const choice = root.querySelector('[data-gift-design]:checked');
        const amount = root.querySelector('[data-gift-value]:checked');
        const recipientName = recipient.querySelector('[name="properties[Recipient name]"]');
        const name = recipientName.disabled ? '' : recipientName.value.trim();
        const note = message.disabled ? '' : message.value.trim();
        const artwork = email.querySelector('[data-gift-email-art]');
        artwork.src = choice.value;
        artwork.alt = choice.dataset.label;
        email.querySelector('[data-gift-email-value]').textContent = amount.dataset.giftEmailValue;
        const nameLine = email.querySelector('[data-gift-email-recipient]');
        if (name) nameLine.textContent = `For ${name}`;
        else nameLine.remove();
        const messageLine = email.querySelector('[data-gift-email-message]');
        messageLine.textContent = note || 'Your next favorite is waiting.';
        messageLine.style.whiteSpace = 'pre-wrap';
        if (!note) {
          messageLine.style.fontFamily = 'Arial,Helvetica,sans-serif';
          messageLine.style.fontSize = '14px';
          messageLine.style.lineHeight = '1.6';
        }
        email.title = 'Gift email preview';
        const notice = email.createElement('p');
        notice.textContent = 'Email preview · Sample code only · No email has been sent';
        notice.style.cssText = 'margin:0;padding:12px 16px;text-align:center;font:12px/1.5 Arial,Helvetica,sans-serif;background:#292522;color:#fff;';
        email.body.prepend(notice);
        const url = URL.createObjectURL(new Blob(['<!doctype html>\n', email.documentElement.outerHTML], { type: 'text/html' }));
        emailPreview.href = url;
        // Let the native target=_blank link open synchronously from this click.
        // No form data is sent to a server or put into the preview URL.
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      });
    }
    // Run before the shared document-level Ajax cart handler.
    form.addEventListener('submit', (event) => {
      updateDates();
      if (!form.checkValidity()) {
        event.preventDefault();
        event.stopPropagation();
        form.reportValidity();
      }
    });
    updateDelivery();
    updateMessage();
    updateDesign({ fillMessage: !message.value.trim() });
  };

  document.querySelectorAll('[data-gift-card-product]').forEach(initialize);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-gift-card-product]').forEach(initialize);
  });
})();
